import React from "react";
import ReactDOM from "react-dom/client";
import { Provider } from "react-redux";
import { Toaster } from "react-hot-toast";
import { ThemeProvider } from "@mui/material/styles";
import { BrowserRouter as Router } from "react-router-dom";
import { HelmetProvider } from "react-helmet-async";
import { Auth0Provider } from "@auth0/auth0-react";

import "./index.css";
import App from "./App";
import reportWebVitals from "./reportWebVitals";
import { store } from "./store";
import theme from "./theme";
import AutoLogout from "./layouts/AutoLogout";
import { appConfig, missingEnvVars } from "./hooks/useConfig";

// Shown instead of the app when a production build is missing required config,
// so a misconfiguration surfaces a clear message rather than an opaque Auth0
// failure or a blank screen. Var names are non-secret and React-escaped.
const ConfigErrorScreen = ({ missing }: { missing: string[] }) => (
  <div
    role="alert"
    style={{
      maxWidth: 640,
      margin: "15vh auto",
      padding: "2rem",
      fontFamily: "sans-serif",
      textAlign: "center",
    }}
  >
    <h1>Application configuration error</h1>
    <p>
      The application cannot start because required configuration is missing. Set
      the following environment variables in the Amplify Console (App settings →
      Environment variables) and redeploy:
    </p>
    <ul style={{ display: "inline-block", textAlign: "left" }}>
      {missing.map((name) => (
        <li key={name}>
          <code>{name}</code>
        </li>
      ))}
    </ul>
  </div>
);

if (window.location.search.includes('iss=')) {
  const url = new URL(window.location.href);
  url.searchParams.delete('iss');
  window.history.replaceState({}, '', url.toString());
}

// Create a root for rendering using ReactDOM.createRoot
const root = ReactDOM.createRoot(
  document.getElementById("root") as HTMLElement
);

const auth0Config = {
  domain: appConfig.AUTH0_DOMAIN,
  clientId: appConfig.AUTH0_CLIENT_ID,
  authorizationParams: {
    redirect_uri: window.location.origin,
    scope: "openid profile email offline_access",
    audience: "PegasusAdminPanel",
  },
  useRefreshTokens: true, // Enable refresh tokens
  cacheLocation: "memory",
};

// A production build with required env vars missing must not mount the app: it
// would initialise Auth0 with empty strings and break with an opaque error at the
// auth step, and could route PHI to a wrong/missing backend. Show a clear message
// and refuse to start instead. Dev builds still render (useConfig warns instead).
if (process.env.NODE_ENV === "production" && missingEnvVars.length) {
  root.render(<ConfigErrorScreen missing={missingEnvVars} />);
} else {
  // Render the application inside the root
  root.render(
    // Wrap the application in React Strict Mode for additional development checks
    <React.StrictMode>
      <Auth0Provider
        domain={auth0Config.domain}
        clientId={auth0Config.clientId}
        authorizationParams={auth0Config.authorizationParams}
        useRefreshTokens={auth0Config.useRefreshTokens}
        cacheLocation={"memory"}
      >
        {/*
          Use the Provider component to make the Redux store accessible to the entire application
        */}
        <Provider store={store}>
          <AutoLogout />
          {/*
          ThemeProvider provides the application with a theme. Wrap the entire application with the App component
        */}
          <ThemeProvider theme={theme}>
            <HelmetProvider>
              <Router>
                <App />
                <Toaster position="bottom-center" />
              </Router>
            </HelmetProvider>
          </ThemeProvider>

          {/* Toaster component for displaying toast notifications */}
        </Provider>
      </Auth0Provider>
    </React.StrictMode>
  );

  // If you want to start measuring performance in your app, pass a function
  // to log results (for example: reportWebVitals(console.log))
  // or send to an analytics endpoint. Learn more: https://bit.ly/CRA-vitals
  reportWebVitals();
}
